import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import moment from 'moment/moment';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { ContentMetadata } from '@candu-ai/content/types';
import { ContentPlacement } from '@candu-ai/placement/types';
import { Snapshot } from '@candu-ai/snapshots/types';
import { useToggle } from 'commons/hooks';

export const useRetakeSnapshotNotification = (
  contentMetadata: ContentMetadata,
  additionalConditions = true,
) => {
  const [notificationVisible, toggleNotificationVisible] = useToggle(true);

  const { getAll: getPlacements } = useCreatePayload(ENDPOINTS.contentPlacements);
  const { getAll: getSnapshots } = useCreatePayload(ENDPOINTS.snapshots);

  const placementsPayload = getPlacements({
    query: { finder: 'byContentId', contentId: contentMetadata.id },
  });

  const { data: placements } = useQuery<ContentPlacement[]>(
    createCacheKey(placementsPayload),
    () => fetchResource(placementsPayload).then(),
    {
      refetchOnWindowFocus: 'always',
    },
  );

  const snapshotsPayload = getSnapshots({});

  const { data: snapshots } = useQuery<Snapshot[]>(
    createCacheKey(snapshotsPayload),
    () =>
      fetchResource<Snapshot[]>(snapshotsPayload).then((res) =>
        res.filter((snap) => snap.contentId === contentMetadata.id),
      ),
    {
      refetchOnWindowFocus: 'always',
    },
  );

  const shouldShowNotification = useMemo(
    () =>
      contentMetadata.visibilityState === 'Live' &&
      !!placements?.length &&
      !!snapshots?.length &&
      !!placements?.find(
        (p) =>
          !!p.snapshotId &&
          moment(p.createdAt) > moment().subtract(30, 'm') &&
          snapshots?.find((s) => s.id === p.snapshotId),
      ),
    [contentMetadata.visibilityState, placements, snapshots],
  );

  return {
    showNotification: additionalConditions && shouldShowNotification && notificationVisible,
    closeNotification: toggleNotificationVisible,
  };
};
